import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { DashboardApi, DashboardConfig } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError } from '../utils/statusShortcuts';
import {
  EMPTY_TOTALS,
  formatRelativeTime,
  getComputedStageMeta,
} from './inquiriesShared';
import StageProgressPill from './StageProgressPill';
import './HenvendelserOverviewPage.css';

const numberFormatter = new Intl.NumberFormat('nb-NO');
const DEFAULT_PERIOD_OPTIONS = [
  { key: 'last30', label: 'Siste 30 dager' },
  { key: 'last7', label: 'Siste 7 dager' },
  { key: 'this_month', label: 'Denne måneden' },
  { key: 'last_month', label: 'Forrige måned' },
  { key: 'this_year', label: 'I år' },
];

const formatNumber = (value) => numberFormatter.format(Number(value || 0));

const getGreetingName = ({ accountProfile, user, activeUser }) => {
  const rawName = accountProfile?.displayName
    || accountProfile?.display_name
    || user?.displayName
    || user?.display_name
    || user?.name
    || activeUser?.displayName
    || user?.email
    || '';
  const displayName = String(rawName || '').trim();
  if (!displayName) return null;
  if (displayName.includes('@')) return displayName.split('@')[0];
  return displayName.split(/\s+/)[0] || displayName;
};

const getTimeAwareGreeting = (name) => {
  if (!name) return null;
  const hour = new Date().getHours();
  const variants = hour >= 5 && hour < 10
    ? [
      `God morgen, ${name}. Klar for en ny dag?`,
      `God morgen, ${name}. Håper dagen starter fint.`,
      `Morgen, ${name}. Her er oversikten din.`,
    ]
    : hour >= 10 && hour < 14
      ? [
        `God formiddag, ${name}. Her er status så langt.`,
        `Hei, ${name}. Håper formiddagen går fint.`,
        `God formiddag, ${name}. La oss se hva som har kommet inn.`,
      ]
      : hour >= 14 && hour < 18
        ? [
          `God ettermiddag, ${name}. Her er dagens henvendelser.`,
          `Hei, ${name}. Håper ettermiddagen er fin.`,
          `God ettermiddag, ${name}. Her er det viktigste akkurat nå.`,
        ]
        : hour >= 18 && hour < 24
          ? [
            `God kveld, ${name}. Her er en rolig oversikt.`,
            `Hei, ${name}. Håper kvelden behandler deg pent.`,
            `God kveld, ${name}. Her er status før du runder av.`,
          ]
          : [
            `Hei, ${name}. Her er oversikten din.`,
            `Hei, ${name}. Fint å se deg her.`,
            `Velkommen tilbake, ${name}.`,
          ];
  return variants[Math.floor(Math.random() * variants.length)];
};

const getDisplayName = (inquiry) => (
  inquiry?.display_name
  || inquiry?.name
  || `Henvendelse #${inquiry?.id || ''}`.trim()
);

const getInquirySubtitle = (inquiry) => {
  const parts = [
    inquiry?.email,
    inquiry?.phone,
  ].filter(Boolean);
  if (parts.length > 0) return parts.join(' · ');
  return inquiry?.ai_summary || inquiry?.summary || 'Ingen oppsummering ennå';
};

const getInitials = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '?';
  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

const resolveProfileImageUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:') || /^https?:\/\//i.test(trimmed)) return trimmed;
  try {
    return new URL(trimmed, DashboardConfig.apiBaseUrl).href;
  } catch {
    return trimmed;
  }
};

const getAssignedMember = (inquiry) => {
  const member = inquiry?.assigned_member || null;
  const label = member?.display_name || member?.displayName || member?.email || 'Ufordelt';
  const profileImageUrl = resolveProfileImageUrl(
    member?.profilePictureUrl
    || member?.profile_picture_url
    || member?.avatarUrl
    || member?.avatar_url
    || member?.photoUrl
    || member?.photo_url
    || null,
  );
  return {
    label,
    profileImageUrl,
    assigned: Boolean(member),
  };
};

const OverviewKpiCard = ({ label, value, meta, tone = 'default' }) => (
  <article className={`henvendelser-kpi-card is-${tone}`}>
    <span className="henvendelser-kpi-label">{label}</span>
    <strong>{value}</strong>
    {meta ? <span className="henvendelser-kpi-meta">{meta}</span> : null}
  </article>
);

const ProductKpiCard = ({ product }) => {
  const isArchived = product?.status === 'archived';
  const isUnmatched = product?.status === 'unmatched';
  const avgProgression = product.avg_progression === null || product.avg_progression === undefined
    ? null
    : Math.max(0, Math.min(100, Math.round(Number(product.avg_progression) || 0)));
  const inboxTarget = product?.product_id
    ? `/henvendelser/innboks?botProductId=${encodeURIComponent(product.product_id)}`
    : '/henvendelser/innboks?botProductId=unmatched';
  return (
    <Link className={`henvendelser-product-card${isArchived ? ' is-archived' : ''}${isUnmatched ? ' is-unmatched' : ''}`} to={inboxTarget}>
      <header>
        <div className="henvendelser-product-title-row">
          {product.color_code ? (
            <span
              className="henvendelser-product-swatch"
              style={{ backgroundColor: product.color_code }}
              aria-hidden="true"
            />
          ) : null}
          <div>
            <h3>{product.product_name || 'Uten produkt'}</h3>
            {product.bot_name ? <p>{product.bot_name}</p> : null}
          </div>
        </div>
        {isArchived ? <span className="henvendelser-status-pill">Arkivert</span> : null}
        {isUnmatched ? <span className="henvendelser-status-pill">Uten produkt</span> : null}
      </header>
      <dl className="henvendelser-product-metrics">
        <div>
          <dt>Totalt</dt>
          <dd>{formatNumber(product.total)}</dd>
        </div>
        <div>
          <dt>Nye</dt>
          <dd>{formatNumber(product.new)}</dd>
        </div>
        <div>
          <dt>Pågår</dt>
          <dd>{formatNumber(product.in_progress)}</dd>
        </div>
        <div>
          <dt>Løst</dt>
          <dd>{formatNumber(product.resolved)}</dd>
        </div>
        <div>
          <dt>Ulest</dt>
          <dd>{formatNumber(product.unread)}</dd>
        </div>
        <div>
          <dt>Løsningsgrad</dt>
          <dd>{formatNumber(product.resolved_rate)}%</dd>
        </div>
        <div>
          <dt>Progresjon</dt>
          <dd>
            {avgProgression === null ? '—' : (
              <span
                className={`henvendelser-product-progress${avgProgression >= 100 ? ' is-complete' : avgProgression > 0 ? ' is-active' : ''}`}
                aria-label={`${formatNumber(avgProgression)}% gjennomsnittlig progresjon`}
              >
                <span className="henvendelser-product-progress-track" aria-hidden="true">
                  <span className="henvendelser-product-progress-fill" style={{ width: `${avgProgression}%` }} />
                </span>
                <strong>{formatNumber(avgProgression)}%</strong>
              </span>
            )}
          </dd>
        </div>
      </dl>
      <footer>
        {product.last_inquiry_at ? `Sist mottatt ${formatRelativeTime(product.last_inquiry_at)}` : 'Ingen henvendelser i valgt periode'}
      </footer>
    </Link>
  );
};

const InquiryListItem = ({ inquiry }) => {
  const stageMeta = getComputedStageMeta(inquiry?.stage, inquiry?.progression);
  return (
    <Link className="henvendelser-case-row" to={`/henvendelser/${inquiry.id}`}>
      <span className="henvendelser-case-main">
        <strong>{getDisplayName(inquiry)}</strong>
        <small>{getInquirySubtitle(inquiry)}</small>
      </span>
      <span className="henvendelser-case-side">
        <StageProgressPill
          label={stageMeta.label}
          progression={inquiry?.progression}
          className={`workflow-badge ${stageMeta.className}`}
        />
        <small>{formatRelativeTime(inquiry.last_activity_at || inquiry.submitted_at || inquiry.created_at)}</small>
      </span>
    </Link>
  );
};

const RecentInquiryTable = ({ inquiries }) => (
  <div className="henvendelser-recent-table" role="table" aria-label="Siste henvendelser">
    <div className="henvendelser-recent-head" role="row">
      <span role="columnheader">Navn</span>
      <span role="columnheader">Produkt</span>
      <span role="columnheader">Tildelt</span>
      <span role="columnheader">Status</span>
    </div>
    {inquiries.map((inquiry) => {
      const stageMeta = getComputedStageMeta(inquiry?.stage, inquiry?.progression);
      const productName = inquiry?.matched_product?.name || inquiry?.product || 'Uten produkt';
      const activityAt = inquiry.last_activity_at || inquiry.submitted_at || inquiry.created_at;
      const assignedMember = getAssignedMember(inquiry);
      return (
        <Link className="henvendelser-recent-row" to={`/henvendelser/${inquiry.id}`} role="row" key={inquiry.id}>
          <span className="henvendelser-recent-name" role="cell">
            <strong>{getDisplayName(inquiry)}</strong>
            <small>{activityAt ? formatRelativeTime(activityAt) : 'Ukjent tidspunkt'}</small>
          </span>
          <span className="henvendelser-recent-product" role="cell">{productName}</span>
          <span className={`henvendelser-recent-assignee${assignedMember.assigned ? '' : ' is-unassigned'}`} role="cell">
            <span className="henvendelser-recent-assignee-avatar" aria-hidden="true">
              {assignedMember.profileImageUrl ? (
                <img src={assignedMember.profileImageUrl} alt="" />
              ) : (
                getInitials(assignedMember.label)
              )}
            </span>
            <span>{assignedMember.label}</span>
          </span>
          <span className="henvendelser-recent-stage" role="cell">
            <StageProgressPill
              label={stageMeta.label}
              progression={inquiry.progression}
              className={`workflow-badge ${stageMeta.className}`}
            />
          </span>
        </Link>
      );
    })}
  </div>
);

export default function HenvendelserOverviewPage() {
  const {
    selectedCompanyId,
    selectedCompany,
    permissions,
    accountProfile,
    user,
    activeUser,
  } = useAuth();
  const companyId = selectedCompanyId || selectedCompany?.id || null;
  const canView = Boolean(permissions?.canViewInquiryOverview || permissions?.canViewInnsikt);

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodKey, setPeriodKey] = useState('last30');

  const loadOverview = useCallback(async () => {
    if (!companyId) {
      setOverview(null);
      return;
    }

    setLoading(true);
    try {
      const data = await DashboardApi.getCompanyInboxOverview(companyId, { period: periodKey });
      setOverview(data || null);
    } catch (error) {
      setOverview(null);
      notifyError(error.message || 'Kunne ikke hente henvendelsesoversikt.', {
        title: 'Kunne ikke hente henvendelser',
        source: 'Henvendelser',
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, periodKey]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const totals = overview?.totals || EMPTY_TOTALS;
  const products = useMemo(
    () => (Array.isArray(overview?.products) ? overview.products : []),
    [overview?.products],
  );
  const recent = useMemo(
    () => (Array.isArray(overview?.recent) ? overview.recent : []),
    [overview?.recent],
  );
  const mine = useMemo(
    () => (Array.isArray(overview?.mine) ? overview.mine : []),
    [overview?.mine],
  );
  const periodOptions = useMemo(() => {
    const options = Array.isArray(overview?.period?.presets) && overview.period.presets.length > 0
      ? overview.period.presets
      : DEFAULT_PERIOD_OPTIONS;
    return options.map((entry) => ({
      key: entry.key || entry.value,
      label: entry.label || entry.key || entry.value,
    })).filter((entry) => entry.key);
  }, [overview?.period?.presets]);
  const periodLabel = periodOptions.find((option) => option.key === periodKey)?.label
    || overview?.period?.label
    || 'Siste 30 dager';
  const greetingName = getGreetingName({ accountProfile, user, activeUser });
  const greetingText = useMemo(() => getTimeAwareGreeting(greetingName), [greetingName]);

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  if (!companyId) {
    return (
      <div className="page">
        <div className="card">
          <h1>Henvendelser</h1>
          <p>Velg et selskap for å se henvendelser.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page henvendelser-overview-page">
      <div className="page-header henvendelser-overview-header">
        <div>
          <p className="page-eyebrow">Henvendelser</p>
          <h1>Henvendelser</h1>
          {greetingText ? <p className="henvendelser-greeting">{greetingText}</p> : null}
          <p className="page-subtitle">
            Oversikt for {periodLabel.toLowerCase()}: {formatNumber(totals.total)} henvendelser i valgt periode.
          </p>
        </div>
        <div className="henvendelser-header-actions">
          <label className="henvendelser-period-select" htmlFor="henvendelser-period">
            <span>Periode</span>
            <select
              id="henvendelser-period"
              className="select select--sm"
              value={periodKey}
              onChange={(event) => setPeriodKey(event.target.value)}
            >
              {periodOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <Link className="button button-secondary" to="/henvendelser/innboks">
            Åpne inbox
          </Link>
        </div>
      </div>

      {loading && !overview ? (
        <section className="card henvendelser-loading-card">
          <div className="loading-spinner" />
          <p>Laster henvendelsesoversikt…</p>
        </section>
      ) : (
        <>
          <section className="henvendelser-kpi-grid" aria-label="Henvendelser KPI">
            <OverviewKpiCard
              label={`Henvendelser ${periodLabel.toLowerCase()}`}
              value={formatNumber(totals.total)}
              meta={`${formatNumber(totals.unread)} uleste`}
              tone="primary"
            />
            <OverviewKpiCard
              label="Løst"
              value={formatNumber(totals.resolved)}
              meta={`${formatNumber(totals.resolved_rate)}% løsningsgrad`}
              tone="success"
            />
            <OverviewKpiCard
              label="Pågår"
              value={formatNumber(totals.in_progress)}
              meta={`${formatNumber(totals.new)} nye`}
              tone="progress"
            />
            <OverviewKpiCard
              label="Ufordelte"
              value={formatNumber(totals.unassigned)}
              meta="Krever fordeling"
              tone="danger"
            />
          </section>

          <section className="henvendelser-products-section">
            <header className="card-header henvendelser-section-header">
              <div>
                <h2>KPI per produkt</h2>
                <p className="card-subtitle">Aktive og arkiverte produkter med henvendelser for {periodLabel.toLowerCase()}.</p>
              </div>
            </header>
            {products.length === 0 ? (
              <div className="henvendelser-empty-state">
                <p>Ingen produkter er opprettet ennå.</p>
              </div>
            ) : (
              <div className="henvendelser-product-grid">
                {products.map((product) => (
                  <ProductKpiCard
                    key={product.product_id ? `product-${product.product_id}` : 'product-unmatched'}
                    product={product}
                  />
                ))}
              </div>
            )}
          </section>

          <section className="henvendelser-lists-grid">
            <article className="card henvendelser-list-card">
              <header className="card-header henvendelser-section-header">
                <div>
                  <h2>Siste henvendelser</h2>
                  <p className="card-subtitle">Nyeste saker i {periodLabel}.</p>
                </div>
                <Link to="/henvendelser/innboks">Se alle</Link>
              </header>
              {recent.length === 0 ? (
                <div className="henvendelser-empty-state">
                  <p>Ingen henvendelser i valgt periode.</p>
                </div>
              ) : (
                <RecentInquiryTable inquiries={recent} />
              )}
            </article>

            <article className="card henvendelser-list-card">
              <header className="card-header henvendelser-section-header">
                <div>
                  <h2>Mine saker</h2>
                  <p className="card-subtitle">Åpne henvendelser tildelt deg.</p>
                </div>
                <Link to="/henvendelser/innboks">Se alle</Link>
              </header>
              {mine.length === 0 ? (
                <div className="henvendelser-empty-state">
                  <p>Ingen åpne saker tildelt deg.</p>
                </div>
              ) : (
                <div className="henvendelser-case-list">
                  {mine.map((inquiry) => <InquiryListItem key={inquiry.id} inquiry={inquiry} />)}
                </div>
              )}
            </article>
          </section>
        </>
      )}
    </div>
  );
}
